import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { DeskTable, ElementTable, ByView } from '../../components/nucleus/Shelf';
import { UserPlus } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { useMarkAttentionStale } from '../../contexts/NotificationContext';
import { useToast } from '../../components/common';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { useFacets, Faceted, FindBar } from '../../components/nucleus/FacetRail';
import { Cell } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import DataTable from '../../components/nucleus/DataTable';
import { PersonFace } from '../../components/nucleus/faces';
import { useOpen } from '../../components/nucleus/Inspector';
import { PersonCell } from '../../components/nucleus/cells';
import { usePlace } from '../../nucleus/place';
import PersonInspector, { personActions, friendNote, lapsed, tagOf, ENDED } from './PersonInspector';
import MembersView from './MembersView';
import PublicPage from '../self/PublicPage';
import { usePeople, useGrantReturn, loadMayKnow, isTied, sharingState } from './peopleData';
import './PeopleSection.css';
import { Empty } from '../../components/nucleus/Empty';
import { ago, dateOf } from '../../nucleus/format';

// the filter rail's facets, per page: what a friend sees and whether that is
// live; which way a follow (or a follow request) goes. A friend's side is
// live, paused or ended (expired or revoked): one side ending leaves a friend a
// friend; only both sides ended makes them past.
const SHARE_WORD = { live: 'Live', paused: 'Paused', expired: 'Expired', revoked: 'Revoked', none: 'Nothing shared' };
const WAY_WORD = { both: 'Each other', following: 'You follow', follower: 'Follow you', followIn: 'Want to follow you', followOut: 'You asked to follow' };
const FACETS = {
    friends: [
        { id: 'sees', title: 'Sees', of: (x) => (x.set === 'friend' && sharingState(x.e.friend?.mine).state === 'live' ? x.e.friend?.realm || null : null) },
        { id: 'sharing', title: 'Your sharing', of: (x) => (x.set === 'friend' ? sharingState(x.e.friend?.mine).state : null), order: ['live', 'paused', 'expired', 'revoked', 'none'], label: (k) => SHARE_WORD[k] || k },
    ],
    following: [{
        id: 'way', title: 'Which way',
        of: (x) => (x.set === 'followIn' || x.set === 'followOut' ? x.set : x.e.following && x.e.follower ? 'both' : x.set),
        order: ['both', 'following', 'follower', 'followIn', 'followOut'], label: (k) => WAY_WORD[k],
    }],
    find: [],
};
const personText = (x) => `${x.e.name} ${x.e.username || ''}`;
const PLACEHOLDER = { friends: 'Filter your friends', following: 'Filter who you follow' };

// A FRIEND'S REALM, as their row says it: what they see while your sharing is
// live, else how it stands (paused, expired, revoked, nothing shared)
const friendRealm = (tie) => {
    const s = sharingState(tie.mine);
    return s.state === 'live' ? tie.realm || 'No realm' : s.label;
};
// A FRIEND'S DOT, when a side is not live: in the panel's tones (a side that
// ended is red, paused or nothing shared waits) and with the panel tag's words
// ("Friend · Yours ended", "Friend · Priya’s sharing ended") as its hover
const friendDot = (e) => {
    if (!lapsed(e.friend)) return null;
    const ended = [e.friend.mine, e.friend.theirs].some((ep) => ENDED.has(sharingState(ep).state));
    const tag = tagOf(e);
    return <span className={`people-lapse${ended ? ' is-danger' : ''}`} title={tag} aria-label={tag} />;
};
const endedDot = <span className="people-lapse is-ended" title="Friendship ended" aria-label="Friendship ended" />;

// WHAT A PERSON IS TO YOU in each set, for its table: the tie (in the panel
// tag's words), the realm it reaches and when it began (a past friend's, when
// it ended)
const TIES = {
    in: (e) => ({ word: tagOf(e), at: e.incoming.at }),
    friend: (e) => ({ word: tagOf(e), realm: friendRealm(e.friend), at: e.friend.since, badge: friendDot(e) }),
    out: (e) => ({ word: tagOf(e), realm: e.sent.realm, at: e.sent.at }),
    past: (e) => ({ word: tagOf(e), at: e.past.ended, badge: endedDot }),
    followIn: (e) => ({ word: tagOf(e), at: e.followAsked.at }),
    following: (e) => ({ word: tagOf(e), realm: e.following.realm, at: e.following.since }),
    follower: (e) => ({ word: tagOf(e), realm: e.follower.realm, at: e.follower.since }),
    followOut: (e) => ({ word: tagOf(e), realm: e.followSent.realm, at: e.followSent.at }),
};
// someone met on Find: their standing, with the realm and date of the tie that counts most
const strongest = (e) => (e.friend ? 'friend' : e.incoming ? 'in' : e.sent ? 'out' : e.following ? 'following'
    : e.follower ? 'follower' : e.followAsked ? 'followIn' : e.followSent ? 'followOut' : e.past ? 'past' : null);
const tieOf = (e) => {
    const set = !e.self && strongest(e);
    return { ...(set ? TIES[set](e) : {}), word: tagOf(e) };
};

const when = (iso) => (iso ? new Date(iso).getTime() : null);

/** People as a table: who, what they are to you, the realm it reaches, since when. */
function PersonTable({ id, label, people, tie, openId, onOpen, actionsOf }) {
    const columns = [
        { id: 'name', label: 'Name', lock: true, min: 180, grow: 2, value: (e) => e.name,
          cell: (e) => (
              <span className="people-table__who">
                  <span className="people-table__face"><PersonFace url={e.avatar} size={24} /></span>
                  <span className="people-table__name">{e.name}</span>
                  {tie(e).badge}
              </span>
          ) },
        { id: 'tie', label: 'Relationship', min: 140, grow: 1.4, value: (e) => tie(e).word || null, cell: (e) => tie(e).word || '—' },
        { id: 'realm', label: 'Realm', min: 110, grow: 1, value: (e) => tie(e).realm || null, cell: (e) => tie(e).realm || '—' },
        { id: 'since', label: 'Since', min: 96, grow: 0, asc: false, value: (e) => when(tie(e).at), cell: (e) => dateOf(tie(e).at) || '—' },
    ];
    return (
        <DataTable
            id={id} label={label} columns={columns} rows={people} keyOf={(e) => e.id}
            currentKey={openId} onOpen={onOpen} actionsOf={actionsOf}
        />
    );
}

// PEOPLE — the people you let in. In your own space: your friends with the
// friend requests either way and the friendships that ended, who you follow and
// who follows you (and asks to), and finding someone new. In a group: its members and (for an admin) who is invited.
export default function PeopleSection() {
    const { page, groupId } = usePlace();
    const { pathname } = useLocation();
    const { switchToPersonal } = useGroup();
    // /people/… names no group, but a space remembered from before can still be
    // active on it (GroupContext trusts only the addresses it lists): the
    // address is the answer, and it says your own space.
    const stray = !!groupId && !pathname.startsWith('/group/');
    useEffect(() => { if (stray) switchToPersonal?.(); }, [stray, switchToPersonal]);
    if (stray) return null;
    if (groupId) return <MembersView key={groupId} invitations={page?.id === 'invitations'} />;
    // your public page and who may follow you: a People page, since it is about who sees you
    if (page?.id === 'public') return <PublicPage />;
    return <PersonalPeople page={page?.id || 'friends'} />;
}

function PersonalPeople({ page }) {
    const { section, pages, go, navigate } = usePlace();
    const location = useLocation();
    const toast = useToast();
    const { user } = useAuth();
    const meId = String(user?.id || user?.user_id || '');
    const data = usePeople();
    const [openId, open, close] = useOpen();
    const [stranger, setStranger] = useState(null); // someone opened by link, not in any list here
    const [seen, setSeen] = useState(() => new Map()); // people met on Find (search, may-know)
    const stale = useMarkAttentionStale();
    // back from the consent page: a friend request accepted there is answered too
    useGrantReturn(() => { data.reload(); stale(); });

    // the page you are on, with the person still open: where the consent page returns to
    const back = `${location.pathname}${openId ? `?open=${openId}` : ''}`;
    const ctx = { navigate, back, toast, reload: data.reload, stale };

    // one entry per person, from whichever list knows them
    const entryOf = (id) => {
        if (!id) return null;
        if (String(id) === meId) return { id: meId, name: user?.username || 'You', username: user?.username, self: true };
        // a tie carries the groups you share (groups_in_common); someone met on
        // Find carries the groups may-know found them in
        const tie = data.entry(id);
        if (tie) return tie;
        return seen.get(String(id)) || (stranger && stranger.id === String(id) ? stranger : null);
    };
    const current = openId && openId !== 'public' ? entryOf(openId) : null;

    // ?request=<follow id>: a notification names the request, not the person. Once the
    // ties are read, open whoever sent or received it.
    const [params, setParams] = useSearchParams();
    const request = params.get('request');
    useEffect(() => {
        if (!request || !data.loaded) return;
        const hit = [...data.people.values()].find((e) => [e.incoming, e.followAsked, e.sent, e.followSent, e.friend, e.following, e.past]
            .some((t) => t && String(t.id) === request));
        setParams((p) => {
            const n = new URLSearchParams(p);
            n.delete('request');
            if (hit) n.set('open', String(hit.id));
            return n;
        }, { replace: true });
    }, [request, data.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!openId || openId === 'public' || !data.loaded || entryOf(openId)) return undefined;
        let live = true;
        api.getUser(openId).then((u) => {
            if (!live || !u) return;
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || 'Someone';
            setStranger({ id: String(u.user_id || u.id || openId), name, username: u.username });
        }).catch(() => {});
        return () => { live = false; };
    }, [openId, data.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // this page's people, each tagged with the list it sits in, through the rail
    const items = page === 'following' ? [
        ...data.followAsks.map((e) => ({ e, set: 'followIn' })), ...data.following.map((e) => ({ e, set: 'following' })),
        ...data.followers.map((e) => ({ e, set: 'follower' })), ...data.followSent.map((e) => ({ e, set: 'followOut' })),
    ]
        : page === 'find' ? [] : [
            ...data.incoming.map((e) => ({ e, set: 'in' })), ...data.friends.map((e) => ({ e, set: 'friend' })),
            ...data.sent.map((e) => ({ e, set: 'out' })), ...data.past.map((e) => ({ e, set: 'past' })),
        ];
    const f = useFacets(items, FACETS[page] || FACETS.friends, { text: personText, placeholder: PLACEHOLDER[page] || PLACEHOLDER.friends });
    const inSet = (set) => f.shown.filter((x) => x.set === set).map((x) => x.e);
    const faceted = (body, notice = null) => <Faceted id={`people-${page || 'friends'}`} rail={f.rail} notice={notice}>{body}</Faceted>;
    const label = pages.find((p) => p.id === page)?.label || 'People';

    const actionsOf = (e) => personActions(e, { ...ctx, back: `${location.pathname}?open=${e.id}` });
    const cell = (e, note, extra = {}) => (
        <Cell key={e.id} actions={actionsOf(e)}>
            <PersonCell
                person={{ name: e.name, avatar: e.avatar }}
                note={note}
                current={String(e.id) === String(openId)}
                title={tagOf(e)}
                onPress={() => open(e.id)}
                {...extra}
            />
        </Cell>
    );

    // THE PAGE'S SETS, as cards or as a table by the view, with one title,
    // count and empty for both: `card` draws a person's card, `tie` their row
    const shelf = (sets) => {
        const shown = sets.filter(Boolean);
        return (
            <ByView
                cards={(
                    <DeskTable top={0} between={40}>
                        {shown.map((s) => (
                            <ElementTable key={s.id} title={s.title} note={s.note} count={s.count} empty={s.empty}>{s.people.map(s.card)}</ElementTable>
                        ))}
                    </DeskTable>
                )}
                rows={(
                    <DeskTable top={0} between={40}>
                        {shown.map((s) => (
                            <TitledSet key={s.id} title={s.title} note={s.note} count={s.count} empty={s.empty}>
                                {s.people.length > 0 && <PersonTable id={s.id} label={s.title} people={s.people} tie={s.tie} openId={openId} onOpen={open} actionsOf={actionsOf} />}
                            </TitledSet>
                        ))}
                    </DeskTable>
                )}
            />
        );
    };

    let body;
    let count;
    let verbs;
    // one of the page's sets, by the list it reads
    const setOf = (key, title, card, more = {}) => ({
        id: `people-${key}`, title, people: inSet(key), count: data.loaded ? inSet(key).length : undefined, card, tie: TIES[key], ...more,
    });
    // a list shows only when it holds anyone: no empty headers
    const some = (key, title, note, card) => inSet(key).length > 0 && setOf(key, title, card, { note });
    // THE PAGE WITH NOBODY ON IT: one short line. Under a filter that matches
    // nobody here, a way to look further: Find, with the words carried over.
    const words = (f.search || '').trim();
    // Under filters alone, a way back: Clear filters.
    const nothing = (line) => {
        if (!data.loaded || f.shown.length > 0) return null;
        if (!f.filtering) return <Empty>{line}</Empty>;
        return (
            <Empty>
                Nobody here matches.{' '}
                {words
                    ? <button type="button" className="nx-link" onClick={() => go('find', `q=${encodeURIComponent(words)}`)}>{`Search all of ofSelf for “${words}”`}</button>
                    : <button type="button" className="nx-link" onClick={f.clear}>Clear filters</button>}
            </Empty>
        );
    };
    if (page === 'following') {
        count = data.loaded ? data.following.length : undefined;
        verbs = null;
        body = faceted(<>
            {shelf([
                some('followIn', 'Want to follow you', 'Accepting lets them see your public page.', (e) => cell(e, `asked ${ago(e.followAsked.at, { dateAfter: 30 })}`)),
                some('following', 'You follow', null, (e) => cell(e, e.follower ? 'you follow each other' : 'you follow them')),
                some('follower', 'Follow you', null, (e) => cell(e, e.following ? 'you follow each other' : 'follows you')),
                some('followOut', 'You asked to follow', null, (e) => cell(e, `asked ${ago(e.followSent.at, { dateAfter: 30 })}`)),
            ])}
            {/* friends are on Friends, not here: an empty page with friends says so */}
            {nothing(data.friends.length ? (<>
                Friends aren’t listed here: a friend already sees your public page.{' '}
                <button type="button" className="nx-link" onClick={() => go('friends')}>See your friends</button>
            </>) : 'You follow nobody, and nobody follows you yet.')}
        </>);
    } else if (page === 'find') {
        verbs = null;
        body = <FindPeople data={data} meId={meId} cell={cell} shelf={shelf} onSeen={setSeen} />;
    } else {
        count = data.loaded ? data.friends.length : undefined;
        verbs = (<>
            <Verb primary icon={<UserPlus />} onClick={() => go('find')}>Add a friend</Verb>
        </>);
        body = faceted(<>
            {shelf([
                some('in', 'Asking you', 'Accept by choosing what you share back. Nothing is shared until you do.',
                    (e) => cell(e, `asked ${ago(e.incoming.at, { dateAfter: 30 })}`)),
                some('friend', 'Friends', 'You each share something.', (e) => cell(e, friendNote(e.friend), { badge: friendDot(e) || undefined })),
                some('out', 'Waiting on them', 'What you offered starts when they accept.', (e) => cell(e, `asked ${ago(e.sent.at, { dateAfter: 30 })}`)),
                some('past', 'Past', 'Friendships that ended. Nothing is shared either way.', (e) => cell(e, `ended ${dateOf(e.past.ended)}`, { badge: endedDot })),
            ])}
            {nothing('No friends yet. Add a friend finds anyone on ofSelf.')}
        </>);
    }

    let inspector = null;
    if (openId === 'public') inspector = <Navigate replace to="/people/public" />; // the old ?open=public
    else if (current) {
        inspector = <PersonInspector key={current.id} entry={current} actions={personActions(current, ctx)} onClose={close} />;
    }

    return (
        <NucleusPage section={section.label} title={label} count={count} verbs={verbs} inspector={inspector}>
            {body}
        </NucleusPage>
    );
}

// FIND PEOPLE: anyone on ofself by username, and the people you may know
// through your groups (read only here — one call per group).
function FindPeople({ data, meId, cell, shelf, onSeen }) {
    // ?q= carries a search over from another page (a filter that matched nobody)
    // and a later one too, while already here
    const [params] = useSearchParams();
    const q = params.get('q');
    const [term, setTerm] = useState(() => q || '');
    const [found, setFound] = useState(null);
    useEffect(() => { if (q) setTerm(q); }, [q]);

    useEffect(() => {
        const t = term.trim();
        if (!t) { setFound(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            api.searchUsers(t, 30)
                .then((r) => { if (live) setFound((Array.isArray(r) ? r : r?.users || []).map(personOf)); })
                .catch(() => { if (live) setFound([]); });
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [term]);

    // people in your groups you have no live tie to yet (the groups you share
    // with someone you are tied to come with the tie: groups_in_common)
    const [mayKnow, setMayKnow] = useState(null);
    const tiedKey = data.loaded ? [...data.people.values()].filter(isTied).map((e) => e.id).sort().join(',') : null;
    useEffect(() => {
        if (tiedKey === null) return undefined;
        let live = true;
        loadMayKnow({ meId, tied: new Set(tiedKey ? tiedKey.split(',') : []) }).then((list) => { if (live) setMayKnow(list); });
        return () => { live = false; };
    }, [tiedKey, meId]);

    // what this page has met, so a press can open anyone in it
    useEffect(() => {
        onSeen((prev) => {
            const n = new Map(prev);
            for (const p of [...(found || []), ...(mayKnow || [])]) n.set(p.id, { ...(n.get(p.id) || {}), ...p });
            return n;
        });
    }, [found, mayKnow, onSeen]);

    const withTies = (p) => { const t = data.entry(p.id); return { ...p, ...(t || {}), groups: p.groups || t?.groups }; };
    const noteOf = (e) => (e.id === meId ? 'you' : tagOf(e).toLowerCase());
    const groupsOf = (p) => p.groups.map((g) => g.name).join(', ');

    return (<>
        <div className="nx-pagefind">
            <FindBar value={term} onChange={setTerm} placeholder="Find anyone on ofSelf by username" />
        </div>
        {shelf([
            found !== null && {
                id: 'people-found', title: `Named like “${term.trim()}”`, count: found.length, empty: 'Nobody by that name.',
                people: found.map((p) => (p.id === meId ? { ...p, self: true } : withTies(p))),
                card: (e) => cell(e, noteOf(e)), tie: tieOf,
            },
            // shown only when it holds anyone
            mayKnow?.length > 0 && {
                id: 'people-may-know', title: 'People you may know', count: mayKnow.length,
                people: mayKnow.map(withTies),
                card: (e) => cell(e, `in ${groupsOf(e)}`),
                // what they are to you is the groups you share
                tie: (e) => ({ ...tieOf(e), word: `In ${groupsOf(e)}` }),
            },
        ])}
    </>);
}

function personOf(u) {
    const id = String(u.user_id || u.id);
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || 'Someone';
    return { id, name, username: u.username || null };
}
